/**
 * "Nearby amenities" for the public property detail page — schools,
 * hospitals, shops etc. within walking distance, queried live from
 * OpenStreetMap's Overpass API given the property's own lat/lng. This is the
 * same data source the app's Leaflet maps already tile from
 * (tile.openstreetmap.org); Overpass is OSM's free, CORS-enabled query
 * endpoint for the underlying point data, not a new third-party dependency
 * class. Best-effort only: any fetch failure just means the section renders
 * nothing, never a page-blocking error, since this is decoration on top of
 * the property's own (already-loaded) data, not core content.
 */

export type AmenityCategory = 'school' | 'hospital' | 'supermarket' | 'restaurant' | 'pharmacy' | 'bank';

const CATEGORY_LABEL: Record<AmenityCategory, string> = {
  school: 'School',
  hospital: 'Hospital',
  supermarket: 'Supermarket',
  restaurant: 'Restaurant',
  pharmacy: 'Pharmacy',
  bank: 'Bank',
};

export interface NearbyAmenity {
  name: string;
  category: AmenityCategory;
  categoryLabel: string;
  distanceMeters: number;
}

interface OverpassElement {
  tags?: Record<string, string>;
  lat?: number;
  lon?: number;
  center?: { lat: number; lon: number };
}

interface OverpassResponse {
  elements: OverpassElement[];
}

const RADIUS_METERS = 1500;
const MAX_RESULTS = 8;
const EARTH_RADIUS_METERS = 6_371_000;

function haversineMeters(a: [number, number], b: [number, number]): number {
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);
  const h = sinLat * sinLat + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * sinLon * sinLon;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
}

export function formatDistance(meters: number): string {
  return meters < 1000 ? `${Math.round(meters / 10) * 10}m` : `${(meters / 1000).toFixed(1)}km`;
}

/** Fetches nearby amenities; resolves to an empty array on any failure rather than throwing, matching this feature's decoration-not-core-content role. */
export async function fetchNearbyAmenities(latitude: number, longitude: number): Promise<NearbyAmenity[]> {
  const categories: AmenityCategory[] = ['school', 'hospital', 'supermarket', 'restaurant', 'pharmacy', 'bank'];
  // A union of exact-match clauses, not a single regex alternation — verified
  // directly against the shared public Overpass instance that the regex form
  // (`["amenity"~"^(a|b|c)$"]`) is expensive enough server-side to reliably
  // 504/429 under load, while this union form returns in ~1-2s. Also
  // node-only: skips amenities mapped as building outlines rather than
  // points, a smaller reliability cost than the query-complexity one.
  const clauses = categories
    .map((category) => `node["amenity"="${category}"](around:${RADIUS_METERS},${latitude},${longitude});`)
    .join('');
  const query = `[out:json][timeout:15];(${clauses});out ${MAX_RESULTS * 4};`;

  try {
    const response = await fetch('https://overpass-api.de/api/interpreter', {
      method: 'POST',
      body: `data=${encodeURIComponent(query)}`,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    if (!response.ok) return [];

    const data = (await response.json()) as OverpassResponse;
    const origin: [number, number] = [latitude, longitude];

    const amenities = data.elements
      .map((element): NearbyAmenity | null => {
        const tags = element.tags;
        if (tags === undefined) return null;
        const category = tags['amenity'] as AmenityCategory | undefined;
        if (category === undefined || !(category in CATEGORY_LABEL)) return null;
        const name = tags['name'];
        if (name === undefined || name === '') return null;

        const point = element.lat !== undefined && element.lon !== undefined
          ? [element.lat, element.lon]
          : element.center !== undefined
            ? [element.center.lat, element.center.lon]
            : null;
        if (point === null) return null;

        return {
          name,
          category,
          categoryLabel: CATEGORY_LABEL[category],
          distanceMeters: haversineMeters(origin, point as [number, number]),
        };
      })
      .filter((amenity): amenity is NearbyAmenity => amenity !== null)
      .sort((a, b) => a.distanceMeters - b.distanceMeters)
      .slice(0, MAX_RESULTS);

    return amenities;
  } catch {
    return [];
  }
}
